import { useEffect, useState } from "react";
import axios from "axios";

/**
 * Special notes on order lines (owner request 2026-10-20).
 *
 * The ready-made notes ("Extra spicy", "No onion") are admin-kept on the server
 * (Admin → Order lists → Item notes). Every punch screen reads them through
 * `useNotePresets`. The last list fetched is kept in localStorage so a CaptainPAD
 * that has lost the server still offers them — typing a note never needs the
 * list, so a failed fetch only means fewer chips, never a blocked punch.
 */

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const CACHE_KEY = "vibez-note-presets";

// The server caps a note at 200 characters; the KOT prints it whole.
export const NOTE_MAX = 200;

const readCache = () => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

const writeCache = list => {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(list));
  } catch {
    // Private window or full storage: the chips simply are not remembered.
  }
};

export function useNotePresets(token) {
  const [presets, setPresets] = useState(readCache);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    axios
      .get(`${API}/item-note-presets`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { active_only: true },
      })
      .then(r => {
        if (!alive) return;
        const labels = (r.data || []).map(p => p.label);
        setPresets(labels);
        setError(null);
        writeCache(labels);
      })
      .catch(failure => {
        if (alive) setError(failure?.message || "Could not load the ready-made notes");
      });
    return () => {
      alive = false;
    };
  }, [token]);
  return { presets, error };
}

/** One spelling of "no note", matching the server: trimmed, empty → "". */
export const cleanNote = note => String(note || "").trim();

/**
 * Tapping a ready-made note adds it to the text, or takes it back out if it is
 * already there. Notes are joined with ", " — that is how a cook reads a list.
 */
export const toggleNotePart = (text, part) => {
  const parts = cleanNote(text)
    .split(",")
    .map(p => p.trim())
    .filter(Boolean);
  const at = parts.findIndex(p => p.toLowerCase() === part.toLowerCase());
  if (at >= 0) parts.splice(at, 1);
  else parts.push(part);
  return parts.join(", ");
};

export const noteHasPart = (text, part) =>
  cleanNote(text)
    .split(",")
    .map(p => p.trim().toLowerCase())
    .includes(part.toLowerCase());

/** The photo URL a screen can put in an <img>, or null. */
export const itemImageSrc = item =>
  item?.image_url ? `${process.env.REACT_APP_BACKEND_URL || ""}${item.image_url}` : null;
